import React, { useState, useEffect } from 'react';
import { getWebAppUrl } from '../../../config/runtimeConfig';
import { Box, Avatar } from '@mui/material';
import { getAllCompanies, deleteCompany, updateCompany } from '../../../services/apiservice';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import CompanyEditModal from './CompanyEditModal';
import { useApiData } from '../../../hooks/useApi';
import DataTable from '../../common/DataTable';

const NoData = () => <span style={{ color: '#ccc' }}>No data</span>;

function CompanyList() {
  const { data: fetchedCompanies, loading } = useApiData(() => getAllCompanies().then((res) => res.data), []);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const webAppUrl = `${getWebAppUrl()}/company/`;

  // Seed local state from the fetched data so the optimistic delete/edit mutations work.
  useEffect(() => {
    if (fetchedCompanies) {
      setCompanies(fetchedCompanies);
    }
  }, [fetchedCompanies]);

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setModalOpen(true);
  };

  const handleDelete = async (company) => {
    const CompanyID = company.CompanyID;
    try {
      setLoadingCompanies(prevState => ({ ...prevState, [CompanyID]: true }));
      const response = await deleteCompany(CompanyID);
      if (response.success) {
        setCompanies(companies.filter(c => c.CompanyID !== CompanyID));
        setSnackbarMessage('Company Deleted successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage(response.message || 'Failed to delete Company.');
        setSnackbarSeverity('error');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
      setLoadingCompanies(prevState => ({ ...prevState, [CompanyID]: false }));
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedCompany(null);
  };

  const handleModalSave = async (updatedCompany) => {
    try {
      const response = await updateCompany(updatedCompany);
      if (response.success) {
        const updatedImageUrl = response.data?.CompanyLogoURL || updatedCompany.CompanyLogoURL;
        setCompanies((prevCompanies) =>
          prevCompanies.map((company) =>
            company.CompanyID === updatedCompany.CompanyID
              ? { ...company, ...updatedCompany, CompanyLogoURL: updatedImageUrl }
              : company
          )
        );
        setSnackbarMessage('Company updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const columns = [
    { key: 'CompanyID', label: 'Company ID', width: 50 },
    {
      key: 'CompanyName',
      label: 'Company Name',
      width: 150,
      render: (row) => (
        <Box display="flex" alignItems="center">
          <Avatar src={row.CompanyLogoURL} sx={{ width: 40, height: 40, marginRight: 2 }} />
          {row.CompanyName ? <span>{row.CompanyName}</span> : <NoData />}
        </Box>
      ),
    },
    { key: 'CompanyType', label: 'Company Type', width: 120 },
    { key: 'CompanyIndustry', label: 'Industry', width: 150 },
    { key: 'CompanyHeadquarters', label: 'Headquarters', width: 150 },
    {
      key: 'profileUrl',
      label: 'Company Profile URL',
      width: 100,
      render: (row) => (
        row.CompanyName ? (
          <span><a href={`${webAppUrl}${row.CompanyName}`} target="_blank" rel="noopener noreferrer"><b>Click Here</b></a></span>
        ) : (
          <NoData />
        )
      ),
    },
  ];

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <DataTable
        title="Companies List"
        columns={columns}
        rows={companies}
        loading={loading}
        searchKeys={['CompanyName', 'CompanyType', 'CompanyIndustry']}
        rowKey="CompanyID"
        onEdit={handleEdit}
        onDelete={handleDelete}
        deleting={loadingCompanies}
      />
      <CompanyEditModal
        open={modalOpen}
        onClose={handleModalClose}
        company={selectedCompany}
        onSave={handleModalSave}
      />
    </>
  );
}

export default CompanyList;
